<template>
    <div class="token-stats-dashboard">
        <div class="trade-activity-header">TRADE ACTIVITY</div>

        <div v-if="isLoading" class="text-center py-4">Loading stats...</div>
        <div v-else-if="error" class="text-center text-danger py-4">{{ error }}</div>
        <div v-else>
            <!-- Time Period Tabs -->
            <div class="time-period-tabs">
                <div v-for="(period, idx) in timePeriods" :key="idx"
                     class="time-tab"
                     :class="{ 'active': activePeriod === period.value }"
                     @click="activePeriod = period.value">
                    {{ period.label }}
                </div>
            </div>

            <!-- Percentage Changes -->
            <div class="percentage-changes">
                <div v-for="(period, idx) in timePeriods" :key="idx" class="percentage-item">
                    <div class="period-label">{{ period.label }}</div>
                    <div class="period-value" :class="getPriceChangeClass(getPercentageForPeriod(period.value))">
                        {{ formatPriceChange(getPercentageForPeriod(period.value)) }}
                    </div>
                </div>
            </div>

            <!-- Compact Stats Grid -->
            <div class="stats-grid">
                <div class="stat-row">
                    <span class="stat-label">Txns</span>
                    <span class="stat-value">{{ formatNumber(activeStats?.total?.trades || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Buys</span>
                    <span class="stat-value text-success">{{ formatNumber(activeStats?.buys?.count || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Sells</span>
                    <span class="stat-value text-danger">{{ formatNumber(activeStats?.sells?.count || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Volume</span>
                    <span class="stat-value">${{ formatPrice(activeStats?.total?.volume_usd || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Buy Vol</span>
                    <span class="stat-value text-success">${{ formatPrice(activeStats?.buys?.volume_usd || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Sell Vol</span>
                    <span class="stat-value text-danger">${{ formatPrice(activeStats?.sells?.volume_usd || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Makers</span>
                    <span class="stat-value">{{ formatNumber(activeStats?.total?.unique_traders || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Buyers</span>
                    <span class="stat-value text-success">{{ formatNumber(activeStats?.buys?.unique_traders || 0) }}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Sellers</span>
                    <span class="stat-value text-danger">{{ formatNumber(activeStats?.sells?.unique_traders || 0) }}</span>
                </div>
            </div>

            <!-- Trade Size Distribution -->
            <div v-if="hasVolumeBuckets" class="size-distribution">
                <div class="distribution-header">VOLUME BY TRADE SIZE</div>
                <div class="distribution-bars">
                    <div v-for="(bucket, key) in sizeBucketConfig" :key="key" class="bucket-row">
                        <span class="bucket-label">{{ bucket.label }}</span>
                        <div class="bucket-bar-container">
                            <div class="bucket-bar" :style="{ width: getBucketPercentage(key) + '%' }" :class="bucket.class"></div>
                        </div>
                        <span class="bucket-value">${{ formatPrice(getBucketVolume(key)) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

const props = defineProps({
    mint: {
        type: String,
        required: true,
    }
});

const stats = ref({});
const isLoading = ref(false);
const error = ref(null);
const activePeriod = ref('24h'); // Default to 24h view
let intervalId = null;

// Define time periods
const timePeriods = [
    { label: '5M', value: '5m' },
    { label: '1H', value: '1h' },
    { label: '6H', value: '6h' },
    { label: '24H', value: '24h' },
    { label: '7d', value: '7d' }
];

// Get active stats based on the selected time period
const activeStats = computed(() => {
    return stats.value[activePeriod.value] || {};
});

// Function to get percentage change for a specific period
const getPercentageForPeriod = (period) => {
    return stats.value[period]?.price_change_percent || 0;
};

const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(num);
};

const formatPrice = (price) => {
    if (price === 0 || !price) return '0.0';
    if (price > 1000000) return (price / 1000000).toFixed(1) + 'M';
    if (price > 1000) return (price / 1000).toFixed(1) + 'K';
    if (price > 1) return price.toFixed(1);
    if (price > 0.01) return price.toFixed(2);
    return price.toPrecision(2);
};

const formatPriceChange = (change) => {
    if (!change) return '0.00%';
    const formattedChange = Math.abs(change).toFixed(2);
    return `${formattedChange}%`;
};

const getPriceChangeClass = (change) => {
    if (!change) return '';
    return change > 0 ? 'text-success' : change < 0 ? 'text-danger' : '';
};

const buyPercentage = (data) => {
    if (!data) return 0;
    const total = (data.buys?.count || 0) + (data.sells?.count || 0);
    return total > 0 ? ((data.buys?.count || 0) / total) * 100 : 0;
};

const sellPercentage = (data) => {
    if (!data) return 0;
    const total = (data.buys?.count || 0) + (data.sells?.count || 0);
    return total > 0 ? ((data.sells?.count || 0) / total) * 100 : 0;
};

const getVolumePercentage = (part, total) => {
    if (!part || !total) return 0;
    return (part / total) * 100;
};

const getMakersPercentage = (part, total) => {
    if (!part || !total) return 0;
    return (part / total) * 100;
};

// Size bucket configuration
const sizeBucketConfig = {
    micro: { label: '<$1', class: 'bucket-micro' },
    small: { label: '$1-$10', class: 'bucket-small' },
    medium: { label: '$10-$100', class: 'bucket-medium' },
    large: { label: '$100-$1K', class: 'bucket-large' },
    whale: { label: '$1K-$10K', class: 'bucket-whale' },
    mega: { label: '$10K-$100K', class: 'bucket-mega' },
    institutional: { label: '>$100K', class: 'bucket-institutional' },
};

// Check if we have size bucket data
const hasVolumeBuckets = computed(() => {
    const buckets = activeStats.value?.size_buckets;
    if (!buckets) return false;
    return Object.values(buckets).some(b => b.volume_usd > 0);
});

// Get total volume across all buckets
const totalBucketVolume = computed(() => {
    const buckets = activeStats.value?.size_buckets;
    if (!buckets) return 0;
    return Object.values(buckets).reduce((sum, b) => sum + (b.volume_usd || 0), 0);
});

// Get volume for a specific bucket
const getBucketVolume = (bucketKey) => {
    return activeStats.value?.size_buckets?.[bucketKey]?.volume_usd || 0;
};

// Get percentage for a bucket bar
const getBucketPercentage = (bucketKey) => {
    const volume = getBucketVolume(bucketKey);
    const total = totalBucketVolume.value;
    return total > 0 ? (volume / total) * 100 : 0;
};

const fetchStats = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        const response = await axios.get(`/api/tokens/${props.mint}/stats`);
        stats.value = response.data;

        // If no data exists for the active period, switch to a period that has data
        if (!stats.value[activePeriod.value]) {
            const availablePeriods = Object.keys(stats.value).filter(key =>
                timePeriods.some(period => period.value === key)
            );
            if (availablePeriods.length > 0) {
                activePeriod.value = availablePeriods[0];
            }
        }
    } catch (err) {
        console.error('Error fetching token stats:', err);
        error.value = 'Failed to load stats.';
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    fetchStats();
    intervalId = setInterval(fetchStats, 30000); // Refresh stats every 30 seconds
});

onUnmounted(() => {
    clearInterval(intervalId);
});
</script>

<style scoped>
/* Compass-styled Trade Activity panel */
.token-stats-dashboard {
    background: var(--compass-bg-secondary, #1a1a2e);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-radius: var(--compass-radius, 12px);
    padding: 0.75rem;
    color: var(--compass-text-primary, #e6edf3);
}

.trade-activity-header {
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--compass-text-primary, #e6edf3);
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
}

/* Time tabs - matching BuySellPressure .time-tab style */
.time-period-tabs {
    display: flex;
    gap: 0.15rem;
    margin-bottom: 0.5rem;
    background: rgba(139, 92, 246, 0.08);
    border-radius: 6px;
    padding: 0.15rem;
}

.time-tab {
    flex: 1;
    padding: 0.3rem 0.5rem;
    border: none;
    background: transparent;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.7rem;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
}

.time-tab:hover:not(.active) {
    color: var(--compass-text-primary, #e6edf3);
    background: rgba(139, 92, 246, 0.1);
}

.time-tab.active {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #ffffff;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.percentage-changes {
    display: flex;
    justify-content: space-between;
    padding: 0.25rem 0 0.5rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.1));
}

.percentage-item {
    text-align: center;
    flex: 1;
}

.period-label {
    font-size: 0.6rem;
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.1rem;
}

.period-value {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.75rem;
    font-weight: 600;
}

/* Compact stats grid - inline label/value */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.15rem 0.5rem;
}

.stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.2rem 0;
    border-bottom: 1px solid rgba(139, 92, 246, 0.08);
}

.stat-row:last-child,
.stat-row:nth-child(3n) {
    border-bottom: none;
}

.stat-label {
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.stat-value {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
}

.text-success {
    color: var(--compass-success, #22c55e) !important;
}

.text-danger {
    color: var(--compass-danger, #ef4444) !important;
}

/* Light mode support - uses body.dark-mode selector */
body:not(.dark-mode) .token-stats-dashboard {
    background: #ffffff;
    border-color: rgba(139, 92, 246, 0.15);
    color: #1f2937;
}

body:not(.dark-mode) .trade-activity-header {
    color: #1f2937;
}

body:not(.dark-mode) .data-value {
    color: #1f2937;
}

body:not(.dark-mode) .time-tab {
    color: #6b7280;
}

body:not(.dark-mode) .time-tab:hover:not(.active) {
    color: #374151;
}

/* IMPORTANT: Preserve white text on active gradient tabs in light mode */
body:not(.dark-mode) .time-tab.active {
    color: #ffffff;
}

body:not(.dark-mode) .time-period-tabs {
    background: rgba(139, 92, 246, 0.05);
}

body:not(.dark-mode) .stat-value {
    color: #1f2937;
}

/* Size Distribution */
.size-distribution {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
}

.distribution-header {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--compass-text-secondary, #7d8590);
    margin-bottom: 0.5rem;
}

.distribution-bars {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.bucket-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.bucket-label {
    font-size: 0.6rem;
    color: var(--compass-text-secondary, #7d8590);
    min-width: 55px;
    text-align: right;
}

.bucket-bar-container {
    flex: 1;
    height: 10px;
    background: rgba(139, 92, 246, 0.1);
    border-radius: 3px;
    overflow: hidden;
}

.bucket-bar {
    height: 100%;
    border-radius: 3px;
    transition: width 0.5s ease;
    min-width: 2px;
}

/* Size bucket colors - gradient from retail to whale */
.bucket-micro { background: linear-gradient(90deg, #6366f1, #818cf8); }
.bucket-small { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.bucket-medium { background: linear-gradient(90deg, #a855f7, #c084fc); }
.bucket-large { background: linear-gradient(90deg, #d946ef, #e879f9); }
.bucket-whale { background: linear-gradient(90deg, #ec4899, #f472b6); }
.bucket-mega { background: linear-gradient(90deg, #f43f5e, #fb7185); }
.bucket-institutional { background: linear-gradient(90deg, #f59e0b, #fbbf24); }

.bucket-value {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.6rem;
    color: var(--compass-text-primary, #e6edf3);
    min-width: 50px;
}

body:not(.dark-mode) .bucket-value {
    color: #1f2937;
}
</style>
